<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import ApplicationMark from '@/Components/ApplicationMark.vue';
import { useHeaderMorph } from '@/Composables/headerMorph.js';
import MainNavigationBar from './MainNavigationBar.vue';
import AccountMenu from './AccountMenu.vue';
import LanguageMenu from './LanguageMenu.vue';
import SearchButton from './SearchButton.vue';
import { focusRing } from './navigationClasses.js';

const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    homeUrl: {
        type: String,
        default: '/',
    },
    locale: {
        type: String,
        default: 'de',
    },
    /** The authenticated user, or `null` for guests. */
    user: {
        type: Object,
        default: null,
    },
    canManageApiTokens: {
        type: Boolean,
        default: false,
    },
    isImpersonating: {
        type: Boolean,
        default: false,
    },
    /** Overrides the scroll driven state, mainly for stories and tests. */
    minified: {
        type: Boolean,
        default: null,
    },
});

const { minified: scrolledPastThreshold } = useHeaderMorph();

const isMinified = computed(() => props.minified ?? scrolledPastThreshold.value);

const morphTransition = 'transition-[height] duration-300 ease-out motion-reduce:transition-none';
</script>

<template>
    <div>
        <header class="fixed inset-x-0 top-0 z-50 bg-gray-50 border-b border-deep-teal-200 purple-theme:border-purple-200 shadow-[0_2px_4px] shadow-deep-teal-700/15 purple-theme:shadow-purple-700/15">
            <div class="container mx-auto px-4">
                <div
                    class="flex items-center gap-6"
                    :class="[morphTransition, isMinified ? 'h-16' : 'h-28']"
                >
                    <div class="flex flex-1">
                        <Link
                            class="shrink-0 rounded-md text-deep-teal-700 purple-theme:text-purple-700"
                            :class="focusRing"
                            :href="homeUrl"
                            :aria-label="$t('components.navigation.home')"
                        >
                            <ApplicationMark
                                class="block w-auto"
                                :class="[morphTransition, isMinified ? 'h-8' : 'h-12']"
                            />
                        </Link>
                    </div>

                    <MainNavigationBar
                        :items
                        :aria-label="$t('components.navigation.main')"
                    />

                    <div class="flex flex-1 items-center justify-end gap-6">
                        <AccountMenu
                            :user
                            :can-manage-api-tokens
                            :is-impersonating
                        />

                        <LanguageMenu :locale />

                        <SearchButton />
                    </div>
                </div>
            </div>
        </header>

        <!--
            The header is taken out of the flow, and this keeps the space it
            needs at full size free. A header that scrolls along with the page
            shrinks its own box when it morphs, which moves everything below it
            up; the browser holds that content still by moving the scroll
            position along, and `useHeaderMorph` then reads that correction as
            a scroll against the one that caused the morph and flips the header
            back — over and over, for as long as the page is scrolled. Out of
            the flow the morph moves nothing, so there is nothing to read back.
        -->
        <div class="h-28" />
    </div>
</template>
